/**
 * Jovii — HOW LONG SOMETHING HAS BEEN LOOKED AT.
 *
 * A trailer costs about 4.7 MB (docs/TRAILERS.md § D1), so nothing starts one
 * until a card has held somebody's attention for a few seconds. That wait used
 * to be a `setTimeout` armed when the focus arrived and torn down when it left
 * — which is correct only if "focus left" means "stopped looking".
 *
 * On a phone it did not. Focus was cleared on every `scroll` and every `resize`
 * and the timer started again from zero, so a person reading with a thumb never
 * accumulated five uninterrupted seconds and the banner's trailer never played
 * (docs/TRAILERS.md § C2 — the owner's iPhone complaint, reproduced on Linux).
 *
 * So the wait is a STOPWATCH, not a countdown. It runs while a card is attended
 * and the screen is still, it PAUSES while the screen is moving, and it keeps
 * what it has banked. Five seconds of attention gathered over eight seconds of
 * a thumb nudging the page is still five seconds of attention — and a card that
 * is genuinely scrolled away loses the focus at the next settle, which throws
 * the stopwatch away entirely.
 *
 * Immutable on purpose: every function returns the next state rather than
 * mutating, so a component can hold one in a plain variable and a test can hold
 * a dozen without them interfering.
 */

/** A stopwatch. `since` is null while it is paused. */
export interface Dwell {
  /** How long the wait is, in ms. */
  needMs: number;
  /** Attention banked before the current run, in ms. */
  spentMs: number;
  /** When the current run started, or null when paused. */
  since: number | null;
}

/** A fresh stopwatch, stopped at zero. */
export function beginDwell(needMs: number): Dwell {
  return { needMs, spentMs: 0, since: null };
}

/** Start counting. Calling it again while it runs changes nothing. */
export function runDwell(d: Dwell, now: number): Dwell {
  if (d.since !== null) return d;
  return { ...d, since: now };
}

/**
 * Stop counting and bank what this run was worth.
 *
 * A clock that has gone backwards — a laptop waking up, a phone changing time
 * zone — banks nothing rather than banking a negative, because the one thing
 * this must never do is make a trailer start earlier than it was asked to.
 */
export function haltDwell(d: Dwell, now: number): Dwell {
  if (d.since === null) return d;
  const run = Math.max(0, now - d.since);
  return { needMs: d.needMs, spentMs: d.spentMs + run, since: null };
}

/** Attention banked so far, including the run in progress. */
export function dwellSpent(d: Dwell, now: number): number {
  return d.since === null ? d.spentMs : d.spentMs + Math.max(0, now - d.since);
}

/** How much longer it has to be looked at. Zero means now. */
export function dwellLeft(d: Dwell, now: number): number {
  return Math.max(0, d.needMs - dwellSpent(d, now));
}

/** True when the wait is over. */
export function dwellDone(d: Dwell, now: number): boolean {
  return dwellLeft(d, now) === 0;
}
